import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import styled from "styled-components/macro";
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { pickBy } from "ramda";
import DroppableAnswerSlot from "./DroppableAnswerSlot";
import DraggableAnswerBlock from "./DraggableAnswerBlock";
import { AppSettingsContext } from "../../AppSettingsContext";

const LeftColumn = styled.div`
  display: block;
  padding: 0 20px 0 0;
  width: 50%;
  box-sizing: border-box;

  @media (max-width: 480px) {
      width: 100%;
      padding: 0;
  }

  @media (min-width: 481px) and (max-width: 768px) {
      width: 100%;
      padding: 0;
  }
`;

const RightColumn = styled.div`
  display: grid;
  justify-content: center;
  padding: 20px;
  width: 50%;
  box-sizing: border-box;
  max-height: 350px;
  height: auto;
  grid-template-columns: 115px 115px 115px;
  align-items: center;
  background: ${({ theme: { colors } }) => colors.EGLightestGrey};

  @media (max-width: 480px) {
      width: 100%;
      padding: 15px 20px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
      width: 100%;
      padding: 15px 20px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: 110px 110px 110px;
  }
`;

const PlaceholderNode = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 17.458px;
  margin: 20px;

  @media (max-width: 480px) {
    width: 60px;  
    height: 60px;
    margin: 5px;
    font-size: ${({ theme: { typography } }) => typography.body.small.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.small.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.small.lineHeight};
    letter: ${({ theme: { typography } }) => typography.body.small.letter};
  }

  @media (min-width: 481px) and (max-width: 768px) {
    width: 60px;  
    height: 60px;
    margin: 5px;
    font-size: ${({ theme: { typography } }) => typography.body.small.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.small.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.small.lineHeight};
    letter: ${({ theme: { typography } }) => typography.body.small.letter};
  }
`;

const LeftDroppableColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: flex-start;
`;
const RightDroppableColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: flex-start;
`;

const DroppableColumnSeperator = styled.div`
  width: 6px;
  border-radius: 100px;
  background: black;
  flex: 0 0 auto;
`;

const DroppableColumnHeader = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: ${({ theme: { typography } }) => typography.body.large.fontSize};
  font-weight: ${({ theme: { typography } }) => typography.body.large.fontWeight};
  line-height: ${({ theme: { typography } }) => typography.body.large.lineHeight};
  letter: ${({ theme: { typography } }) => typography.body.large.letter};
  color: ${({ theme: { colors } }) => colors.Black};
  width: 100%;
  text-align: center;

  @media (max-width: 480px) {
    font-size: ${({ theme: { typography } }) => typography.body.medium.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.medium.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.medium.lineHeight};
    letter: ${({ theme: { typography } }) => typography.body.medium.letter};
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: ${({ theme: { typography } }) => typography.body.medium.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.medium.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.medium.lineHeight};
    letter: ${({ theme: { typography } }) => typography.body.medium.letter};
  }
`;

const DroppableSlotContainer = styled.div`
  display: grid;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  max-height: 350px;
  height: auto;
  grid-template-columns: 140px 140px 140px;

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: 110px 110px 110px;
  }
`;

const StyledImageContainer = styled.div`
  width: 100%;
  height: auto;
  overflow: hidden;
  display: flex;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
  }
`;

const StyledCardText = styled.p`
  margin: 0;
  padding: 5px 0;
`;

const StyledSlotContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const StyledSlotHeading = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: ${({ theme: { typography } }) => typography.headline.small.fontSize};
  font-weight: ${({ theme: { typography } }) => typography.headline.small.fontWeight};
  line-height: ${({ theme: { typography } }) => typography.headline.small.lineHeight};
  color: ${({ theme: { colors } }) => colors.Black};
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: ${({ theme: { typography } }) => typography.body.medium.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.medium.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.medium.lineHeight};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    font-size: ${({ theme: { typography } }) => typography.body.large.fontSize};
    font-weight: ${({ theme: { typography } }) => typography.body.large.fontWeight};
    line-height: ${({ theme: { typography } }) => typography.body.large.lineHeight};
  }
`;

const QuizCardMatchupC = forwardRef(
  (
    {
      onChange,
      isError,
      createCodeButtonText,
      enterCodeButtonText,
      isDisabled,
      onSubmitCode,
      copyMode,
      questionConfig,
      questionId,
      onReadyToSubmitChange,
      onQuestionStateChange,
      ...props
    },
    ref,
  ) => {
    const { assetServer } = useContext(AppSettingsContext);
    const [dropParent, setDropParent] = useState({});

    function handleDragEnd(event) {
      if (event.over && event.over.id) {
        const isCorrectAnswer = questionConfig.solution[
          event.active.id
        ].includes(event.over.id);
        if (!isCorrectAnswer) {
          return;
        }
        setDropParent((state) => {
          var stateWithDropParentRemoved = pickBy((k, v) => {
            return k !== event.active.id;
          }, state);
          return {
            ...stateWithDropParentRemoved,
            [`${event.over.id}`]: event.active.id,
          };
        });
      }
    }

    useImperativeHandle(ref, () => ({
      validateQuestion: () => {
        var answerResponse = Object.entries(questionConfig.solution).map(
          ([k, v]) => {
            const dropParentInQuestion = Object.entries(dropParent).find(
              ([dpk, dpv]) => dpv === k,
            );
            const [dpk, dpv] = dropParentInQuestion;
            return { slotId: dpk, answerId: k, isCorrect: v.includes(dpk) };
          },
        );
        if (answerResponse.every((r) => r.isCorrect)) {
          console.log("Quiz answer passed");
          onQuestionStateChange("Done");
        } else {
          console.log("Quiz answer failed");
          onQuestionStateChange("Failed");
        }
        console.log(answerResponse);
      },
    }));

    const dropParentValues = useMemo(
      () => Object.values(dropParent),
      [dropParent],
    );

    const answerMarkeup = Object.entries(questionConfig.answers).reduce(
      (accumulator, [answerKey, answerValue]) => {
        accumulator[answerKey] = (
          <DraggableAnswerBlock
            key={questionId+answerKey+answerValue.text}
            isDropped={dropParentValues.includes(answerKey)}
            id={answerKey}
          >
            <>
              {answerValue.image !== "" && 
                <StyledImageContainer>
                  <img src={`${assetServer}/${answerValue.image}`} alt={`${answerValue.imageAlt}`} title={`${answerValue.imageAlt}`} />
                </StyledImageContainer>
              }
              <StyledCardText>{answerValue.text}</StyledCardText>
            </>
          </DraggableAnswerBlock>
        );
        return accumulator;
      },
      {},
    );

    const dropSlots = questionConfig.groupOne.slots.map((item) => (
      <StyledSlotContainer>
        <StyledSlotHeading>{item.slotHeading}</StyledSlotHeading>
        <DroppableAnswerSlot id={item.slotId} key={questionId+item.slotId}>
          {dropParent[item.slotId] ? answerMarkeup[dropParent[item.slotId]] : null}
        </DroppableAnswerSlot>
      </StyledSlotContainer>
    ));

    const submitEnabled =
      Object.keys(dropParent).length >= dropSlots.length;

    useEffect(() => {
      if(onReadyToSubmitChange)
      {
        onReadyToSubmitChange(submitEnabled);
      }
    }, [submitEnabled]);

    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);

    const sensors = useSensors(
      mouseSensor,
      touchSensor,
    );

    return (
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <LeftColumn>
          <DroppableSlotContainer>{dropSlots}</DroppableSlotContainer>
        </LeftColumn>
        <RightColumn>
          {Object.entries(answerMarkeup).map(([key, value]) =>
            !Object.values(dropParent).includes(key) ? (
              value
            ) : (
              <PlaceholderNode />
            ),
          )}
        </RightColumn>
      </DndContext>
    );
  },
);

export default QuizCardMatchupC;
